import * as React from 'react'
import { cn } from '@/lib/utils'

type SwitchProps = {
    checked?: boolean
    defaultChecked?: boolean
    onCheckedChange?: (checked: boolean) => void
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'>

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
    (
        {
            className,
            checked,
            defaultChecked = false,
            disabled,
            onCheckedChange,
            ...props
        },
        ref
    ) => {
        const [internalChecked, setInternalChecked] =
            React.useState(defaultChecked)
        const isControlled = checked !== undefined
        const value = isControlled ? checked : internalChecked

        const handleToggle = () => {
            if (disabled) return
            const next = !value
            if (!isControlled) setInternalChecked(next)
            onCheckedChange?.(next)
        }

        return (
            <button
                type="button"
                role="switch"
                aria-checked={value}
                data-state={value ? 'checked' : 'unchecked'}
                className={cn(
                    'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-[#17A2B8]',
                    className
                )}
                onClick={handleToggle}
                disabled={disabled}
                ref={ref}
                {...props}
            >
                <span
                    data-state={value ? 'checked' : 'unchecked'}
                    className={cn(
                        'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0'
                    )}
                />
            </button>
        )
    }
)
Switch.displayName = 'Switch'

export { Switch }
